import { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { Loader2, Search, Users, Repeat, TrendingUp, Wallet, ChevronRight, Bike, X } from "lucide-react";

const Stat = ({ icon: Icon, label, value, hint }) => (
  <div className="surface-card p-4 sm:p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="size-4 text-spark-teal" />
      <p className="label-mono text-spark-teal-dark">{label}</p>
    </div>
    <p className="font-display uppercase tracking-tight text-2xl sm:text-3xl font-bold text-spark-ink">{value}</p>
    {hint && <p className="text-xs text-spark-slate mt-1">{hint}</p>}
  </div>
);

export default function AdminCustomers() {
  const [data, setData] = useState({ customers: [], analytics: null });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const searchRef = useRef(null);

  useEffect(() => {
    let dead = false;
    api.get("/admin/customers").then((res) => {
      if (dead) return;
      setData(res.data || { customers: [], analytics: null });
    }).catch(() => toast.error("Couldn't load customers")).finally(() => !dead && setLoading(false));
    return () => { dead = true; };
  }, []);

  // Keyboard shortcut: "/" (or ⌘K / Ctrl+K) focuses the search input.
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.matches("input, textarea")) return;
      if (e.key === "/" || ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k")) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const scootersText = (scooters) =>
    (scooters || []).map((s) => `${s.brand || ""} ${s.model || ""}`.trim()).filter(Boolean).join(" · ");

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return data.customers;
    return data.customers.filter((c) => {
      if ((c.email || "").toLowerCase().includes(qq)) return true;
      if ((c.name || "").toLowerCase().includes(qq)) return true;
      if ((c.phone || "").toLowerCase().includes(qq)) return true;
      // Match against every scooter brand/model the customer has ever brought in.
      for (const s of c.scooters || []) {
        if ((s.brand || "").toLowerCase().includes(qq)) return true;
        if ((s.model || "").toLowerCase().includes(qq)) return true;
      }
      return false;
    });
  }, [data.customers, q]);

  const a = data.analytics || { total_customers: 0, repeat_customers: 0, repeat_rate_pct: 0, total_revenue: 0, avg_lifetime_value: 0 };
  const showingCount = filtered.length;
  const totalCount = data.customers.length;

  return (
    <AdminLayout title="Customers" eyebrow="CRM">
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="size-6 animate-spin text-spark-yellow" /></div>
      ) : (
        <div className="space-y-6" data-testid="admin-customers-page">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <Stat icon={Users} label="Total customers" value={a.total_customers} />
            <Stat icon={Repeat} label="Repeat customers" value={a.repeat_customers} hint={`${a.repeat_rate_pct}% repeat rate`} />
            <Stat icon={Wallet} label="Lifetime revenue" value={`$${(a.total_revenue || 0).toLocaleString()}`} />
            <Stat icon={TrendingUp} label="Avg lifetime value" value={`$${(a.avg_lifetime_value || 0).toFixed(2)}`} />
          </div>

          <div className="surface-card p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-spark-slate" />
                <input
                  ref={searchRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name · phone · email · scooter brand or model…"
                  className="input-style pl-9 pr-9"
                  data-testid="customers-search"
                  aria-label="Search customers"
                />
                {q && (
                  <button
                    type="button"
                    onClick={() => { setQ(""); searchRef.current?.focus(); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-spark-slate hover:text-spark-ink hover:bg-spark-floor"
                    aria-label="Clear search"
                    data-testid="customers-search-clear"
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </div>
              <kbd className="hidden md:inline-flex items-center gap-1 px-1.5 py-1 rounded-sm bg-spark-floor border border-spark-line text-[10px] font-mono text-spark-slate select-none">Press <span className="font-bold text-spark-ink">/</span></kbd>
            </div>
            <p className="text-xs text-spark-slate mb-4" data-testid="customers-count">
              {q ? (
                <>Showing <strong className="text-spark-ink">{showingCount}</strong> of {totalCount} · match on name, phone, email or scooter</>
              ) : (
                <>{totalCount} customer{totalCount === 1 ? "" : "s"} on file</>
              )}
            </p>

            {filtered.length === 0 ? (
              <p className="text-center py-10 text-spark-slate text-sm">
                {data.customers.length === 0
                  ? "No customers yet — they appear here automatically when you take bookings or shop orders."
                  : (
                    <>No matches for &ldquo;<strong className="text-spark-ink">{q}</strong>&rdquo;. Try a shorter phrase or a brand name.</>
                  )}
              </p>
            ) : (
              <ul className="divide-y divide-spark-line" data-testid="customers-list">
                {filtered.map((c) => (
                  <li key={c.email}>
                    <Link
                      to={`/admin/customers/${encodeURIComponent(c.email)}`}
                      className="flex items-center gap-3 sm:gap-4 py-3.5 group hover:bg-spark-floor -mx-4 sm:-mx-5 px-4 sm:px-5 transition rounded-sm"
                      data-testid={`customer-row-${c.email}`}
                    >
                      <div className="size-10 rounded-full bg-spark-teal/15 text-spark-teal flex items-center justify-center font-bold uppercase text-sm shrink-0">
                        {(c.name || c.email).slice(0, 1).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-spark-ink truncate">{c.name || "(no name)"}</p>
                          {c.do_not_service && (
                            <span className="px-1.5 py-0.5 rounded-sm bg-red-600 text-white text-[9px] uppercase tracking-wider font-bold shrink-0" data-testid={`customer-flagged-${c.email}`}>Do not service</span>
                          )}
                          {c.first_seen && (
                            <span className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-spark-floor border border-spark-line text-[9px] uppercase tracking-wider font-bold text-spark-slate shrink-0" data-testid={`customer-since-${c.email}`}>
                              Since {new Date(c.first_seen).getFullYear()}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-spark-slate truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
                        {c.scooters?.length > 0 && (
                          <p className="text-[11px] text-spark-teal-dark mt-0.5 truncate flex items-center gap-1" data-testid={`customer-scooters-${c.email}`}>
                            <Bike className="size-3 shrink-0" /> {scootersText(c.scooters)}
                          </p>
                        )}
                      </div>
                      <div className="hidden sm:flex items-center gap-4 text-xs">
                        <span className="text-spark-slate"><strong className="text-spark-ink font-display uppercase">{c.jobs_count}</strong> jobs</span>
                        <span className="text-spark-slate"><strong className="text-spark-ink font-display uppercase">{c.orders_count}</strong> orders</span>
                        <span className="text-spark-slate"><strong className="text-spark-ink font-display uppercase">${(c.total_spend || 0).toFixed(2)}</strong></span>
                      </div>
                      <ChevronRight className="size-4 text-spark-slate group-hover:text-spark-teal transition shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
